
import { useState, useContext } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { GlobalContext } from "../../Context/GlobalContext";

const useOrderSetting = () => {
  const { baseUrl, basicAuth, userID } = useContext(GlobalContext);
  const [loading, setLoading] = useState(false);
  const [printerList, setPrinterList] = useState([]);

  const config = {
    headers: {
      Authorization: basicAuth,
      "Content-Type": "application/json",
    },
  };

  const handleApi = async (url, body, successMsg, errorMsg) => {
    setLoading(true);
    try {
      const res = await axios.post(`${baseUrl}${url}`, body, config);

      if (res?.data?.responseCode === "00") {
        if (successMsg) toast.success(successMsg);
        return res.data;
      } else {
        toast.error(res?.data?.responseMessage || errorMsg);
        return null;
      }
    } catch (err) {
      toast.error(errorMsg || "Something went wrong");
      return null;
    } finally {
      setLoading(false);
    }
  };

  // ---------------- DELIVERY ----------------
  const updateDeliveryCharges = (payload) =>
    handleApi(
      "/Order/v1.0/updateDeliveryCharges",
      { userid: userID, ...payload },
      "Delivery charges updated",
    );

  const getDeliveryCharges = () =>
    handleApi("/Order/v1.0/getDeliveryChargesAndDiscount", { userid: userID });

  const deleteDeliveryCharges = (chargeId) =>
    handleApi(
      "/Order/v1.0/deleteDeliveryCharges",
      { userid: userID, chargeId },
      "Deleted successfully",
    );

  // ---------------- DISCOUNT ----------------
  const updateDiscount = async (payload) => {
    const res = await handleApi(
      "/Order/v1.0/updateDiscount",
      { userid: userID, ...payload },
      "Discount updated",
    );

    return res;
  };

  const deleteDiscount = async (discountId) => {
    const res = await handleApi(
      "/Order/v1.0/deleteDiscount",
      { userid: userID, discountId },
      "Discount deleted",
    );

    return res;
  };

  // ---------------- COUPON ----------------
  const createCoupon = (payload) =>
    handleApi(
      "/Order/v1.0/createMerchantCouponCode",
      { userid: userID, ...payload },
      "Coupon created",
    );

  const getCoupons = () =>
    handleApi("/Order/v1.0/getMerchantCouponCodes", { userid: userID });

  const deleteCoupon = (couponId, operation = "DELETE") =>
    handleApi(
      "/Order/v1.0/disableMerchantCouponCode",
      { userid: userID, couponId, operation },
      "Coupon updated",
    );

  const toggleCouponStatus = (couponId, operation) =>
    handleApi(
      "/Order/v1.0/disableMerchantCouponCode",
      { userid: userID, couponId, operation },
      "Coupon updated",
    );

  // ---------------- LOYALTY ----------------
  const getLoyaltyPoints = () =>
    handleApi("/Order/v1.0/getMerchantLoyaltyPointsConfig", { userid: userID });

  const updateLoyaltyPoints = (payload) =>
    handleApi(
      "/Order/v1.0/updateMerchantLoyaltyPointsConfig",
      { userid: userID, ...payload },
      "Loyalty updated",
    );

  // ---------------- RESTAURANT TIMING ----------------
  const getRestaurantTiming = async () => {
    const res = await handleApi(
      "/Order/v1.0/getRestaurantTiming",
      {
        userid: String(userID),
      },
      "",
      "Failed to fetch restaurant timing",
    );

    if (!res) {
      return {
        success: false,
        data: null,
        message: "Failed to fetch restaurant timing",
      };
    }

    const timingData = res?.response || res?.data || res?.result || null;

    return {
      success: true,
      data: timingData,
      message:
        res?.responseMessage ||
        res?.message ||
        "Restaurant timing fetched successfully",
    };
  };

  const updateRestaurantTiming = async ({ opening, closing, weekOff }) => {
    const res = await handleApi(
      "/Order/v1.0/updateRestaurantTiming",
      {
        userid: String(userID),
        opening: String(opening || ""),
        closing: String(closing || ""),
        weekOff: String(weekOff || ""),
      },
      "Restaurant timing updated successfully",
      "Failed to update restaurant timing",
    );

    if (!res) {
      return {
        success: false,
        message: "Failed to update restaurant timing",
      };
    }

    return {
      success: true,
      data: res?.response || res?.data || res?.result || null,
      message:
        res?.responseMessage ||
        res?.message ||
        "Restaurant timing updated successfully",
    };
  };

  // ---------------- Merchant menu item ----------------
  const getMerchantMenuItems = () =>
    handleApi("/Order/v1.0/getMerchantMenuItems", { userid: userID });

  // ---------------- SERVICES ----------------
  const getServices = () =>
    handleApi("/Order/v1.0/getOrderTypeOptions", { userid: userID });

  const updateService = (orderType, status) =>
    handleApi(
      "/Order/v1.0/updateOrderTypeOptions",
      {
        userid: userID,
        orderType,
        status,
      },
      "Service updated",
    );

  // ---------------- ORDER CHARGES ----------------
  const getOrderCharges = () =>
    handleApi("/Order/v1.0/getOrderCharges", { userid: userID });

  const updateOrderCharges = (payload) =>
    handleApi(
      "/Order/v1.0/updateOrderCharges",
      { userid: userID, ...payload },
      "Order charges updated",
    );

  // ---------------- PACKAGING ----------------
  const getPackaging = () =>
    handleApi("/Order/v1.0/getPackagingChargesAndTriggers", { userid: userID });

  const updatePackaging = (payload) =>
    handleApi(
      "/Order/v1.0/UpdatePackagingChargesAndTriggers",
      { userid: userID, ...payload },
      "Packaging updated",
    );

  // ---------------- PRINT ----------------
  const getPrintSetting = () =>
    handleApi("/Order/v1.0/getPrintSettings", { userid: userID });

  const updatePrintSettings = (payload) =>
    handleApi(
      "/Order/v1.0/updatePrintSetting",
      { userid: userID, ...payload },
      "Print settings updated",
    );

  // ---------------- FREE ITEM ----------------
  const addFreeItem = (payload) =>
    handleApi(
      "/Order/v1.0/addFreeItems",
      { userid: userID, ...payload },
      "Free item added",
    );

  // ---------------- PRINTER ----------------
  const addPrinter = (payload) =>
    handleApi(
      "/Order/v1.0/addPrinter",
      { userid: userID, ...payload },
      "Printer added successfully",
    );

  const getPrinters = async () => {
    const res = await handleApi("/Order/v1.0/getPrinters", {
      userid: userID,
    });

    console.log("PRINTER API RAW:", res); // debug

    if (res?.responseCode === "00") {
      setPrinterList(res.response || []);
    }

    return res;
  };

  const getPrinterSettingAbhi = () =>
    handleApi("/Order/getPrinterSettingAbhi", { userid: userID });

  const addPrintSettingAbhi = (payload) =>
    handleApi(
      "/Order/v1.0/AddPrintSettingAbhi",
      { userid: userID, ...payload },
      "Print type updated",
    );

  const normalizeRestaurantTimings = (data) => {
    const raw = data?.result || data?.response || data?.data || [];

    if (!Array.isArray(raw)) return [];

    return raw.filter((item) => item?.status === "1" || item?.status === 1);
  };

  const getRestaurantTimingsV2 = async (restaurantId) => {
    setLoading(true);

    try {
      const res = await axios.post(
        `${baseUrl}/Admin/manageRestuarantTimings`,
        {
          restaurantId: String(restaurantId),
          requests: [],
        },
        {
          headers: {
            Authorization: basicAuth,
            "Content-Type": "application/json",
            flag: "GET",
          },
        },
      );

      const data = res.data;

      const isSuccess =
        data?.status === "1" ||
        data?.status === 1 ||
        data?.responseCode === "00";

      if (isSuccess) {
        return {
          success: true,
          data: normalizeRestaurantTimings(data),
          message: data?.msg || data?.responseMessage || "Timings fetched",
        };
      }

      toast.error(
        data?.msg ||
          data?.responseMessage ||
          "Failed to fetch restaurant timings",
      );

      return {
        success: false,
        data: [],
        message:
          data?.msg ||
          data?.responseMessage ||
          "Failed to fetch restaurant timings",
      };
    } catch (e) {
      console.error("getRestaurantTimingsV2 error:", e);
      toast.error("Error fetching restaurant timings");

      return {
        success: false,
        data: [],
        message: "Error fetching restaurant timings",
      };
    } finally {
      setLoading(false);
    }
  };

  const saveRestaurantTimingsV2 = async ({ restaurantId, requests }) => {
    setLoading(true);

    try {
      const res = await axios.post(
        `${baseUrl}/Admin/manageRestuarantTimings`,
        {
          restaurantId: String(restaurantId),
          requests,
        },
        {
          headers: {
            Authorization: basicAuth,
            "Content-Type": "application/json",
            flag: "INSERT",
          },
        },
      );

      const data = res.data;

      const isSuccess =
        data?.status === "1" ||
        data?.status === 1 ||
        data?.responseCode === "00";

      if (isSuccess) {
        toast.success(data?.msg || data?.responseMessage || "Timings saved");

        return {
          success: true,
          message: data?.msg || data?.responseMessage || "Timings saved",
          data: data?.result || data?.response || [],
        };
      }

      toast.error(
        data?.msg ||
          data?.responseMessage ||
          "Failed to save restaurant timings",
      );

      return {
        success: false,
        message:
          data?.msg ||
          data?.responseMessage ||
          "Failed to save restaurant timings",
      };
    } catch (e) {
      console.error("saveRestaurantTimingsV2 error:", e);
      toast.error("Failed to save restaurant timings");

      return {
        success: false,
        message: "Failed to save restaurant timings",
      };
    } finally {
      setLoading(false);
    }
  };

  // ---------------- WHATSAPP MOBILE ----------------
  const updateMobileWhatsapp = (payload) =>
    handleApi(
      "/Order/updateMobileWhatsapp",
      {
        user_code: userID,
        ...payload,
      },
      "WhatsApp mobile number updated successfully",
      "Failed to update WhatsApp mobile number",
    );

  return {
    loading,
    // delivery
    updateDeliveryCharges,
    getDeliveryCharges,
    deleteDeliveryCharges,

    // discount
    updateDiscount,
    deleteDiscount,

    // coupon
    createCoupon,
    getCoupons,
    deleteCoupon,

    // loyalty
    getLoyaltyPoints,
    updateLoyaltyPoints,

    // timing
    getRestaurantTiming,
    updateRestaurantTiming,

    // services
    getServices,
    updateService,

    // order charges
    getOrderCharges,
    updateOrderCharges,

    // packaging
    getPackaging,
    updatePackaging,

    // print
    getPrintSetting,
    updatePrintSettings,
    addPrinter,
    getPrinters,
    getPrinterSettingAbhi,
    addPrintSettingAbhi,
    printerList,

    getMerchantMenuItems,
    toggleCouponStatus,
    addFreeItem,

    getRestaurantTimingsV2,
    saveRestaurantTimingsV2,
    updateMobileWhatsapp,
  };
};;

export default useOrderSetting;
